import React, { useEffect, useState } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, IconButton, LinearProgress, Tooltip } from "@mui/material";
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { Add, Edit, RadioButtonChecked, RadioButtonUnchecked } from '@mui/icons-material';

import FormikControl from '../../../../../../components/FormikControl/FormikControl';
import productServices from '../productServices';
import { useAlert } from '../../../../../../context/AlertContext';
import { useAppData } from '../../../../../../context/AppDataContext';
import HSNSuggest from './HSNSuggest';
import InlineSystemMasterForm from '../../../../../master/components/InlineSystemMasterForm';

function ProductForm({ data, callBack, callBackWithId }) {
    const { AppAlert } = useAlert();
    const { masters, companyLocationDD } = useAppData();
    
    const [formValues, setFormValues] = useState(null);
    const [dialogStatus, setDialogStatus] = useState(false);
    const [savingInProgress, setSavingStatus] = useState(false);
    const [hsn, setHsn] = useState("");
    const [billingType, setBillingType] = useState(1);
    const [location, setLocation] = useState("");

    const [standardPricing, setStandardPricing] = useState(false);
    const [showDetails, setShowDetails] = useState(false);

    const initialValues = {
        title: "",
        details: "",
        sku: "",
        brandId: 1,
        model: "",
        categoryId: 1,
        color: "",
        uom: 1,
        inventoryType: 1,
        expenseType: 1,
        gstRate: "",
        cost: "0.00",
        price: "0.00",
        mrp: "0.00",
    };

    const updateData = () => {
        let autoFill = {
            id: data.id,
            title: data.title,
            details: data.details,
            sku: data.sku,
            brandId: data.brandId,
            model: data.model,
            categoryId: data.categoryId,
            color: data.color,
            uom: data.uom,
            inventoryType: data.inventoryType,
            expenseType: data.expenseType,
            gstRate: data.gstRate,
            cost: data.cost,
            price: data.price,
            mrp: data.mrp,
        };
        setHsn(data.hsn || "");
        setBillingType(data.billingType || 1);
        
        // Auto-expand sections if data exists
        if (Number(data.cost) > 0 || Number(data.price) > 0 || Number(data.mrp) > 0) {
            setStandardPricing(true);
        }
        if (data.sku || data.color || data.model || data.details) {
            setShowDetails(true);
        }

        setFormValues(autoFill);
        setDialogStatus(true);
    };

    const validationRule = Yup.object({
        title: Yup.string().required("Product name is required"),
        brandId: Yup.string().required("Brand is required"),
        categoryId: Yup.string().required("Category is required"),
        uom: Yup.string().required("UOM is required"),
        gstRate: Yup.number()
            .typeError("GST must be a valid number")
            .min(0, "GST rate cannot be less than 0%")
            .max(100, "GST rate cannot exceed 100%")
            .required("GST Rate is required"),
        cost: Yup.string().required("Cost is required"),
        price: Yup.string().required("Selling price is required"),
        mrp: Yup.string().required("MRP is required"),
    });

    const addNew = () => {
        setHsn("");
        setBillingType(1);
        setLocation("");
        setStandardPricing(false);
        setShowDetails(false);
        setFormValues(initialValues);
        setDialogStatus(true);
    };

    const handleClose = () => {
        setDialogStatus(false);
    };

    const onSubmit = (values) => {
        let payload = { ...values };
        payload.hsn = hsn;
        payload.billingType = billingType;

        if (billingType === 1 && location === "" && !data) {
            AppAlert("A default godown / warehouse must be selected before proceeding.", "error");
            return;
        }

        if (!data && location !== "") {
            payload.location = location;
        }

        setSavingStatus(true);

        productServices.save(payload).then(res => {
            AppAlert(res.msg);
            setDialogStatus(false);
            if (callBackWithId) {
                callBackWithId(res.id);
            } else {
                callBack();
            }
        }).catch(error => {
            AppAlert(error.msg || "An error occurred while saving.", "error");
        }).finally(() => {
            setSavingStatus(false);
        });
    };

    useEffect(()=>{
        if(companyLocationDD.length > 0){
            setLocation(companyLocationDD[0].value)
        }
    },[dialogStatus])

    return (
        <>
            {!data && (
                <Tooltip title="Add New Product or Service">
                    <button type="button" className="btn btn-primary d-flex align-items-center gap-1" onClick={addNew}>
                        <Add fontSize="small" /> Product / Service
                    </button>
                </Tooltip>
            )}
            {data && (
                <Tooltip title="Update Product">
                    <IconButton onClick={updateData} size="small" color="primary">
                        <Edit fontSize="small" />
                    </IconButton>
                </Tooltip>
            )}

            <Dialog
                open={dialogStatus}
                keepMounted
                onClose={handleClose}
                fullWidth={true}
                maxWidth="lg"
                disableAutoFocus={true}
            >
                <Formik
                    initialValues={formValues || initialValues}
                    validationSchema={validationRule}
                    onSubmit={onSubmit}
                    enableReinitialize 
                    validateOnMount
                >
                    {(formik) => (
                        <Form>
                            <DialogTitle className="border-bottom pb-3 mb-3">
                                <h4 className="m-0 font-weight-bold">{data ? "Edit Product / Service" : "Add New Product / Service"}</h4>
                            </DialogTitle>
                            
                            <DialogContent className="px-4">
                                {savingInProgress && <LinearProgress className="mb-3" />}

                                <div className='row gx-4'>
                                    {/* Left Column: Core Info & Supply Type */}
                                    <div className="col-lg-6 border-end-lg pe-lg-4">
                                        <h5 className="text-primary mb-3">Basic Information</h5>
                                        {dialogStatus &&
                                            <div className="mb-3">
                                                <FormikControl control="input" formik={formik} type="text" autoFocus
                                                    label="Product Name" name="title" 
                                                />
                                            </div>  
                                        }
                                        
                                        <div className='mb-3'>
                                            <FormikControl control="autoselect" formik={formik} 
                                                label="Under (Product Category) " name="categoryId" 
                                                options={masters['product-category']} multiple={false} 
                                            />
                                        </div>

                                        <div className="mb-3">
                                            <HSNSuggest 
                                                hsn={hsn} setHsn={setHsn} 
                                                billingType={billingType} setBillingType={setBillingType} 
                                            />
                                        </div>
                                        
                                        
                                        <div className="row">
                                            <div className="col-md-6 mb-3">
                                                <FormikControl control="input" formik={formik} type="number" autoFocus
                                                    label="GST Rate (%)" name="gstRate"   
                                                />
                                            </div>
                                            <div className="col-md-6 mb-3">
                                                <FormikControl control="autoselect" formik={formik} 
                                                    label="UOM (Unit of Measure)" name="uom"  
                                                    options={masters['product-uom']} multiple={false} 
                                                />
                                            </div>
                                        </div>

                                        
                                    </div>

                                    {/* Right Column: Pricing & Additional Details */}
                                    <div className="col-lg-6 ps-lg-4 mt-4 mt-lg-0">
                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                            <h5 className="text-primary m-0">Pricing</h5>
                                            {!standardPricing && (
                                                <button type="button" className='btn btn-sm btn-outline-primary' onClick={(e) => { e.preventDefault(); setStandardPricing(true); }}>
                                                    + Set Standard Pricing
                                                </button>
                                            )}
                                        </div>

                                        {standardPricing ? (
                                            <div className='row bg-light p-3 rounded mb-4'>
                                                
                                                <div className="col-md-4 mb-2">
                                                    <FormikControl control="input" formik={formik} type="number" label="Selling Price" name="price" autoFocus />
                                                </div>
                                                
                                                <div className="col-md-4 mb-2"  >
                                                    <FormikControl control="input" formik={formik} type="number" label="Cost" name="cost" />
                                                </div>
                                                
                                                <div className="col-md-4 mb-2">
                                                    <FormikControl control="input" formik={formik} type="number" label="MRP" name="mrp" />
                                                </div>
                                            </div>
                                        ) : (
                                            <p className="text-muted small mb-4">Standard pricing is hidden. Click the button above to add cost, price, and MRP.</p>
                                        )}

                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                            <h5 className="text-primary m-0">Additional Information</h5>
                                            {!showDetails && (
                                                <button type="button" className='btn btn-sm btn-outline-primary' onClick={ (e) => { e.preventDefault(); setShowDetails(true); }}>
                                                    + Additional Details
                                                </button>
                                            )}
                                        </div>
                                        
                                        {showDetails ? (
                                            <div className='bg-light p-3 rounded'>
                                                <div className="mb-3">
                                                    <FormikControl control="input" formik={formik} type="text" multiline={true} autoFocus
                                                        label="Description" name="details" rows={2}
                                                    />
                                                </div>
                                            
                                                <div className='row'>
                                                    <div className="col-md-6 mb-3">
                                                        <FormikControl control="input" formik={formik} type="text" label="SKU" name="sku" />
                                                    </div>
                                                    <div className="col-md-6 mb-3">
                                                        <FormikControl control="input" formik={formik} type="text" label="Color" name="color" />
                                                    </div>
                                                </div>

                                                <div className='row'>
                                                    <div className='col-md-6 mb-3'>
                                                        <FormikControl control="autoselect" formik={formik} 
                                                            label="Product Brand" name="brandId"  
                                                            options={masters['product-brand']} multiple={false} 
                                                        />
                                                        <div className="mt-1">
                                                            <InlineSystemMasterForm masterName={"product-brand"} callBack={(v) => formik.setFieldValue("brandId", v)} />
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6 mb-3">
                                                        <FormikControl control="input" formik={formik} type="text" label="Model" name="model" />
                                                    </div>
                                                </div>

                                                <div className='row'>
                                                    <div className="col-md-6 mb-3">
                                                        <FormikControl control="autoselect" formik={formik} 
                                                            label="Inventory Type" name="inventoryType"  
                                                            options={masters['inventory-type']} multiple={false} 
                                                        />
                                                    </div>
                                                    <div className="col-md-6 mb-3">
                                                        <FormikControl control="autoselect" formik={formik} 
                                                            label="Expense Type" name="expenseType"  
                                                            options={masters['expense-type']} multiple={false} 
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <p className="text-muted small">Optional fields like SKU, Brand, Model, and Inventory type are hidden.</p>
                                        )}

                                        <div className='card bg-light border-0 p-3 mt-3'>
                                            {/*
                                            <label className='form-label font-weight-bold mb-2'>Type of Supply</label>
                                            <div className='d-flex gap-3'>
                                                <button 
                                                    type="button" 
                                                    className={`btn ${billingType === 1 ? 'btn-primary' : 'btn-outline-secondary'} flex-grow-1 d-flex align-items-center justify-content-center gap-2`}
                                                    onClick={() => setBillingType(1)}
                                                >
                                                    {billingType === 1 ? <RadioButtonChecked fontSize="small" /> : <RadioButtonUnchecked fontSize="small" />} Goods
                                                </button>
                                                <button 
                                                    type="button" 
                                                    className={`btn ${billingType === 2 ? 'btn-primary' : 'btn-outline-secondary'} flex-grow-1 d-flex align-items-center justify-content-center gap-2`}
                                                    onClick={() => setBillingType(2)}
                                                >
                                                    {billingType === 2 ? <RadioButtonChecked fontSize="small" /> : <RadioButtonUnchecked fontSize="small" />} Services
                                                </button>
                                            </div>
                                            */}
                                            {billingType === 1 && (            
                                                <div className="">
                                                    <label>Default Godown / Warehouse</label>
                                                    <select className='form-control' value={location} 
                                                        onChange={(e) => setLocation(e.target.value)}>
                                                        
                                                        {companyLocationDD.map(loc => (
                                                            <option key={loc.value} value={loc.value}>{loc.label}</option>
                                                        ))}
                                                    </select>
                                                </div>                    
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </DialogContent>

                            <DialogActions className="px-4 py-3 border-top">
                                <button className="btn btn-outline-secondary px-4" type="button" onClick={handleClose}>
                                    Cancel
                                </button>
                                <button className="btn btn-primary px-4" type="submit" disabled={savingInProgress}>
                                    {savingInProgress ? "Saving..." : "Save Product"}
                                </button>
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            </Dialog>
        </>
    );
}

export default ProductForm;